import React, { useState, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { ChevronDown, ChevronUp } from 'lucide-react';
import bearLogo from '../../assets/bear-logo.png';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

// ── Inline spinner ──────────────────────────────────────────────────────────
function Spinner() {
  return (
    <>
      <style>{`@keyframes ls { to { transform: rotate(360deg); } }`}</style>
      <div style={{
        width: 36, height: 36,
        border: '3px solid rgba(255,255,255,0.2)',
        borderTopColor: 'var(--color-accent)',
        borderRadius: '50%',
        animation: 'ls .6s linear infinite',
      }} />
    </>
  );
}

// ── Responsive CSS ──────────────────────────────────────────────────────────
// Mobile-first: full dark screen, no card, form directly on background.
// Desktop (≥ 480px): white card centered on the dark background.
const CSS = `
  @keyframes ls { to { transform: rotate(360deg); } }

  /* ── Default: full-screen mobile layout ── */
  .lg-root {
    width: 100vw;
    height: 100dvh;
    min-height: 100dvh;
    overflow: hidden;
    background: var(--color-header);
    display: flex;
    flex-direction: column;
  }

  .lg-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  .lg-brand {
    flex: 0 0 40vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
  }
  .lg-logo  { height: 160px; width: auto; object-fit: contain; }
  .lg-title { font-size: 2.5rem; font-weight: 800; color: #fff; margin: 0; }
  .lg-sub   { font-size: 1.1rem; color: rgba(255,255,255,0.6); margin: 0; }

  .lg-form {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 0 32px;
    gap: 20px;
  }

  .lg-field { display: flex; flex-direction: column; gap: 6px; }
  .lg-label { font-size: 1.1rem; font-weight: 600; color: rgba(255,255,255,0.85); }

  .lg-input {
    width: 100%;
    box-sizing: border-box;
    height: 64px;
    padding: 0 18px;
    font-size: 1.2rem;
    border-radius: 16px;
    border: 1.5px solid rgba(255,255,255,0.2);
    background: rgba(255,255,255,0.1);
    color: #fff;
    outline: none;
    -webkit-appearance: none;
    transition: border-color .15s, background .15s;
  }
  .lg-input::placeholder { color: rgba(255,255,255,0.4); }
  .lg-input:focus {
    border-color: var(--color-accent);
    background: rgba(255,255,255,0.15);
    outline: none;
  }

  .lg-btn {
    width: 100%;
    height: 68px;
    margin-top: 8px;
    background: var(--color-accent);
    color: #fff;
    border: none;
    border-radius: 16px;
    font-size: 1.2rem;
    font-weight: 800;
    cursor: pointer;
    transition: opacity .15s;
  }
  .lg-btn:disabled { opacity: 0.55; cursor: not-allowed; }
  .lg-btn:not(:disabled):active { opacity: 0.85; }

  .lg-error {
    background: rgba(239,68,68,0.15);
    border: 1px solid rgba(239,68,68,0.4);
    border-radius: 12px;
    padding: 12px 16px;
    color: rgba(255,255,255,0.9);
    font-size: 1rem;
    line-height: 1.5;
  }
  .lg-debug-pre {
    margin-top: 0.5rem;
    font-size: 0.8125rem;
    background: rgba(0,0,0,0.25);
    border-radius: 6px;
    padding: 0.625rem;
    white-space: pre-wrap;
    word-break: break-all;
    font-family: monospace;
    line-height: 1.5;
    color: inherit;
  }

  .lg-footer {
    margin-top: auto;
    padding: 24px 32px 48px;
    text-align: center;
    font-size: 1.1rem;
    color: rgba(255,255,255,0.6);
  }
  .lg-footer a { color: var(--color-accent); font-weight: 700; text-decoration: none; }

  /* ── Card layout for tablets and desktops (600px+) ── */
  @media (min-width: 600px) {
    .lg-root {
      width: auto;
      height: auto;
      min-height: 100dvh;
      overflow: visible;
      align-items: center;
      justify-content: center;
      padding: 2rem;
    }
    .lg-body {
      flex: unset;
      width: 100%;
      max-width: 420px;
      background: #fff;
      border-radius: 20px;
      padding: 2.5rem;
      box-shadow: 0 24px 64px rgba(0,0,0,0.35);
    }
    .lg-brand { flex: unset; height: auto; padding-bottom: 1.75rem; }
    .lg-logo  { height: 80px; }
    .lg-title { color: var(--color-text); font-size: 1.75rem; }
    .lg-sub   { color: var(--color-muted); font-size: 1rem; }
    .lg-form  { flex: unset; padding: 0; gap: 1rem; }
    .lg-label { font-size: 0.9375rem; color: var(--color-text); }
    .lg-input {
      height: 52px;
      font-size: 1.125rem;
      border-radius: 12px;
      background: var(--color-bg);
      border: 1.5px solid var(--color-border);
      color: var(--color-text);
    }
    .lg-input::placeholder { color: var(--color-muted); }
    .lg-input:focus { background: #fff; }
    .lg-btn { height: 56px; font-size: 1.125rem; font-weight: 700; border-radius: 14px; margin-top: 8px; }
    .lg-footer { font-size: 1rem; padding-bottom: 0; color: var(--color-muted); margin-top: 1.5rem; }
    .lg-footer a { color: var(--color-accent); }
    .lg-error { background: #fff0f0; border: 1px solid #fecaca; color: var(--color-danger); }
    .lg-debug-pre { background: #fee2e2; }
  }
`;

// ── Component ────────────────────────────────────────────────────────────────

export default function Login() {
  const { signIn, profile, loading: authLoading, profileError, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email,     setEmail]     = useState('');
  const [password,  setPassword]  = useState('');
  const [error,          setError]          = useState('');
  const [debugSql,       setDebugSql]       = useState('');
  const [showDebug,      setShowDebug]      = useState(false);
  const [loading,        setLoading]        = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail,         setResetEmail]         = useState('');
  const [resetSent,          setResetSent]          = useState(false);
  const [magicLinkSending,   setMagicLinkSending]   = useState(false);
  const [magicLinkSent,      setMagicLinkSent]      = useState(false);

  const accessDeniedMessage = location.state?.message || '';

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('error') === 'no_account') {
      setError('No account found for that Google account. Please contact Bear Security Group at info@bearsecurity.group to get access.');
    }
  }, []);

  const handleResetPassword = async () => {
    if (!resetEmail) return;
    const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
      redirectTo: 'https://app.bearsecurity.group/reset-password',
    });
    if (!error) setResetSent(true);
  };

  const handleGoogleSignIn = async () => {
    setError('');
    const { error: oauthErr } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (oauthErr) setError(oauthErr.message);
  };

  const handleSendMagicLink = async () => {
    if (!email) {
      setError('Please enter your email address first.');
      return;
    }
    setMagicLinkSending(true);
    const { error: otpErr } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/client-portal`,
      },
    });
    if (otpErr) {
      setError(otpErr.message);
    } else {
      setMagicLinkSent(true);
    }
    setMagicLinkSending(false);
  };

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setDebugSql('');
    setShowDebug(false);
    setMagicLinkSent(false);
    setLoading(true);
    const { error: err } = await signIn(email, password);
    setLoading(false);
    if (err) {
      if (err.message?.toLowerCase().includes('invalid login')) {
        setError('Email or password not recognized. If you signed up with Google or used a login link before, use "Send me a login link" below.');
      } else {
        setError(err.message || 'Login failed. Please try again.');
      }
    }
    // Redirect is handled by the useEffect below once profile loads
  }

  const rlsFixSql = useCallback(() =>
    `-- Run in Supabase SQL editor to fix RLS:\n\n` +
    `-- 1. Non-recursive admin helper:\n` +
    `create or replace function is_admin()\n` +
    `returns boolean language sql security definer stable\n` +
    `set search_path = public as $$\n` +
    `  select exists (select 1 from profiles where id = auth.uid() and role = 'admin')\n` +
    `$$;\n\n` +
    `-- 2. Replace recursive policies:\n` +
    `drop policy if exists "Admin full access to profiles" on profiles;\n` +
    `drop policy if exists "Officers read/update own profile" on profiles;\n` +
    `create policy "Users manage own profile" on profiles for all\n` +
    `  using (id = auth.uid()) with check (id = auth.uid());\n` +
    `create policy "Admins manage all profiles" on profiles for all\n` +
    `  using (is_admin()) with check (is_admin());`,
  []);

  React.useEffect(() => {
    if (profile) {
      if (profile.role === 'admin')               navigate('/admin/dashboard',   { replace: true });
      else if (profile.role === 'office_manager') navigate('/manager/dashboard', { replace: true });
      else if (profile.role === 'client')         navigate('/client-portal',     { replace: true });
      else if (!profile.onboarding_complete)      navigate('/onboarding',        { replace: true });
      else if (profile.status === 'pending' || profile.status === 'waitlisted') navigate('/officer/pending', { replace: true });
      else                                        navigate('/officer/dashboard',  { replace: true });
      return;
    }

    if (user && !authLoading && !profile) {
      if (profileError?.code === 'NO_ROWS') {
        setError(
          'Your profile couldn\'t be loaded. This can happen if your account was just confirmed — ' +
          'try signing out and back in. If the problem persists, ask your administrator.'
        );
        setDebugSql(rlsFixSql());
      } else if (profileError) {
        setError(`Sign-in succeeded but profile fetch failed. Code: ${profileError.code} — ${profileError.message}`);
        setDebugSql('');
      } else {
        setError('Your account was created but no profile row was found. Ask your administrator to set up your account.');
        setDebugSql(
          `-- Insert a profile row manually:\n` +
          `INSERT INTO profiles (id, email, role, status, onboarding_complete)\n` +
          `VALUES ('${user.id}', '${user.email}', 'admin', 'active', true);`
        );
      }
    }
  }, [profile, profileError, user, authLoading, navigate, rlsFixSql]);

  // Show spinner only while actively loading — not after a profile error
  if (user && (authLoading || (!profile && !profileError))) {
    return (
      <div style={{
        minHeight: '100dvh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        background: 'var(--color-header)', gap: '1rem',
      }}>
        <Spinner />
        <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9375rem' }}>Loading your profile…</p>
      </div>
    );
  }

  return (
    <div className="lg-root">
      <style>{CSS}</style>

      <div className="lg-body">
        {/* Brand */}
        <div className="lg-brand">
          <img src={bearLogo} alt="BearSecurity" className="lg-logo" />
          <h1 className="lg-title">BearSecurity</h1>
          <p className="lg-sub">SecureShift</p>
        </div>

        {/* Portal access denied banner */}
        {accessDeniedMessage && (
          <div style={{
            margin: '0 32px 0', padding: '0.875rem 1rem',
            background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.35)',
            borderRadius: 12, display: 'flex', alignItems: 'flex-start', gap: '0.625rem',
          }}>
            <span style={{ fontSize: '1rem', flexShrink: 0 }}>🔒</span>
            <div style={{ flex: 1 }}>
              <p style={{ margin: 0, fontSize: '0.9375rem', color: '#fff', fontWeight: 600, lineHeight: 1.4 }}>
                {accessDeniedMessage}
              </p>
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.875rem', color: 'rgba(255,255,255,0.75)', lineHeight: 1.4 }}>
                Please contact Bear Security Group at{' '}
                <a href="mailto:info@bearsecurity.group" style={{ color: 'var(--color-accent)', fontWeight: 600 }}>
                  info@bearsecurity.group
                </a>
              </p>
            </div>
          </div>
        )}

        {showForgotPassword ? (
          /* ── Forgot password form ── */
          <div className="lg-form" style={{ paddingTop: '0.5rem' }}>
            <div>
              <p style={{ fontSize: '1.125rem', fontWeight: 700, marginBottom: '0.5rem' }}>Reset your password</p>
              <p style={{ fontSize: '0.9375rem', opacity: 0.7, marginBottom: '1.25rem', lineHeight: 1.5 }}>
                Enter your email and we'll send you a reset link.
              </p>
              <input
                type="email"
                placeholder="Email address"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                className="lg-input"
                style={{ marginBottom: 12 }}
              />
              {resetSent ? (
                <p style={{ color: '#4ade80', fontSize: '1rem', textAlign: 'center', margin: '1rem 0' }}>
                  Check your email for a reset link.
                </p>
              ) : (
                <button type="button" onClick={handleResetPassword} className="lg-btn">
                  Send reset link
                </button>
              )}
            </div>
            <p className="lg-footer">
              <span
                onClick={() => { setShowForgotPassword(false); setResetSent(false); setResetEmail(''); }}
                style={{ cursor: 'pointer', textDecoration: 'underline' }}
              >
                Back to login
              </span>
            </p>
          </div>
        ) : (
          <>
            {/* ── Login form ── */}
            <form className="lg-form" onSubmit={handleSubmit}>
              {/* Google Sign-In */}
              <button
                type="button"
                onClick={handleGoogleSignIn}
                style={{
                  width: '100%', padding: '12px', borderRadius: 8,
                  border: '1px solid #334155', background: '#1e293b',
                  color: '#e2e8f0', fontSize: 14, fontWeight: 600,
                  cursor: 'pointer', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', gap: 10,
                }}
              >
                <svg width="18" height="18" viewBox="0 0 48 48">
                  <path fill="#FFC107" d="M43.6 20H24v8h11.3C33.6 33.1 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20c11 0 19.7-8 19.7-20 0-1.3-.1-2.7-.1-4z"/>
                  <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.5 15.1 18.9 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
                  <path fill="#4CAF50" d="M24 44c5.2 0 9.9-1.9 13.5-5l-6.2-5.2C29.4 35.5 26.8 36 24 36c-5.2 0-9.6-2.9-11.3-7.1l-6.6 4.9C9.8 39.7 16.4 44 24 44z"/>
                  <path fill="#1976D2" d="M43.6 20H24v8h11.3c-.9 2.4-2.5 4.4-4.6 5.8l6.2 5.2C40.7 35.5 44 30.2 44 24c0-1.3-.1-2.7-.4-4z"/>
                </svg>
                Continue with Google
              </button>

              {/* Divider */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ flex: 1, height: 1, background: '#334155' }} />
                <span style={{ fontSize: 12, color: '#94a3b8' }}>or sign in with email</span>
                <div style={{ flex: 1, height: 1, background: '#334155' }} />
              </div>

              <div className="lg-field">
                <label className="lg-label">Email</label>
                <input
                  className="lg-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                  required
                />
              </div>

              <div className="lg-field">
                <label className="lg-label">Password</label>
                <input
                  className="lg-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                />
              </div>

              {error && (
                <div className="lg-error">
                  <p style={{ margin: 0, lineHeight: 1.5 }}>{error}</p>
                  {debugSql && (
                    <div style={{ marginTop: '0.75rem' }}>
                      <button
                        type="button"
                        onClick={() => setShowDebug((v) => !v)}
                        style={{
                          background: 'none', border: 'none', padding: 0,
                          display: 'flex', alignItems: 'center', gap: '0.25rem',
                          color: 'inherit', fontSize: '0.875rem', fontWeight: 700,
                          cursor: 'pointer', textDecoration: 'underline',
                        }}
                      >
                        {showDebug ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                        {showDebug ? 'Hide' : 'Show'} admin troubleshooting SQL
                      </button>
                      {showDebug && (
                        <pre className="lg-debug-pre">{debugSql}</pre>
                      )}
                    </div>
                  )}
                  {error.toLowerCase().includes('not recognized') || error.toLowerCase().includes('invalid') ? (
                    <div style={{
                      marginTop: 12,
                      background: '#0f172a', border: '1px solid #334155',
                      borderRadius: 8, padding: '12px 14px',
                    }}>
                      <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 8, lineHeight: 1.6 }}>
                        If you signed in with Google previously, use the magic link option below to get back in without a password.
                      </div>
                      {!magicLinkSent ? (
                        <button
                          type="button"
                          onClick={handleSendMagicLink}
                          disabled={magicLinkSending}
                          style={{
                            width: '100%', padding: '10px', borderRadius: 8,
                            border: '1px solid #4f8ef7', background: 'transparent',
                            color: '#4f8ef7', fontSize: 13, fontWeight: 600,
                            cursor: magicLinkSending ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {magicLinkSending ? 'Sending...' : '✉ Send me a login link instead'}
                        </button>
                      ) : (
                        <div style={{ color: '#4ade80', fontSize: 13 }}>
                          ✓ Login link sent to {email} — check your inbox.
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
              )}

              <button type="submit" className="lg-btn" disabled={loading}>
                {loading ? 'Signing in…' : 'Sign In'}
              </button>

              <p style={{ textAlign: 'center', marginTop: 4, fontSize: '0.9375rem' }}>
                <span
                  onClick={() => setShowForgotPassword(true)}
                  style={{ opacity: 0.65, cursor: 'pointer', textDecoration: 'underline' }}
                >
                  Forgot your password?
                </span>
              </p>
            </form>

            <p className="lg-footer">
              New officer?{' '}
              <Link to="/register">Request access</Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
